const STORAGE_KEY = 'selfServePromoSeries';
const INTENT_SYNCED_KEY_PREFIX = 'selfServeCampaignIntentSynced';
/** @deprecated Legacy global marker; cleared on persist when storage works */
const LEGACY_INTENT_SYNCED_KEY = 'selfServeCampaignIntentSynced';

const PROMO_SERIES_PARAM = 'promoSeries';

const BUILD_TIME_PROMO_SERIES = String(
  process.env.NEXT_PUBLIC_SELF_SERVE_PROMO_SERIES || '',
).trim();

/** Survives when sessionStorage is blocked or throws */
let memoryPromoSeries = null;

/** `${userId}:${campaignId}` when sessionStorage unavailable */
const memoryIntentSynced = new Set();

function intentSyncedStorageKey(userId, campaignId) {
  return `${INTENT_SYNCED_KEY_PREFIX}:${userId}:${campaignId}`;
}

function persistPromoSeries(series) {
  if (!series) return;
  memoryPromoSeries = series;
  try {
    sessionStorage.setItem(STORAGE_KEY, series);
    sessionStorage.removeItem(LEGACY_INTENT_SYNCED_KEY);
  } catch {
    // sessionStorage unavailable — memoryPromoSeries still set
  }
}

function readPromoSeriesFromUrl() {
  if (typeof window === 'undefined') {
    return null;
  }
  return (
    new URLSearchParams(window.location.search)
      .get(PROMO_SERIES_PARAM)
      ?.trim() || null
  );
}

/**
 * Active campaign id for landing + checkout UI and Stripe checkout.
 *
 * Priority (first match wins):
 * 1. `?promoSeries=` — HGPRO redirect (persisted for checkout continuity)
 * 2. Build-time `NEXT_PUBLIC_SELF_SERVE_PROMO_SERIES` — direct self-serve visits (no session required)
 * 3. sessionStorage / memory — same browser session after HGPRO capture
 */
export function resolveActivePromoSeries() {
  const fromUrl = readPromoSeriesFromUrl();
  if (fromUrl) {
    persistPromoSeries(fromUrl);
    return fromUrl;
  }

  if (BUILD_TIME_PROMO_SERIES) {
    return BUILD_TIME_PROMO_SERIES;
  }

  if (memoryPromoSeries) {
    return memoryPromoSeries;
  }

  return getStoredPromoSeries();
}

/**
 * Reads promoSeries from the current URL and persists it for the subscription flow.
 */
export function capturePromoSeriesFromUrl() {
  if (typeof window === 'undefined') {
    return null;
  }

  const series = readPromoSeriesFromUrl();
  if (series) {
    persistPromoSeries(series);
    return series;
  }

  return resolveActivePromoSeries();
}

export function getStoredPromoSeries() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return sessionStorage.getItem(STORAGE_KEY)?.trim() || null;
  } catch {
    return memoryPromoSeries;
  }
}

export function markCampaignIntentSynced(userId, campaignId) {
  const trimmedUserId = userId?.trim();
  const trimmedCampaignId = campaignId?.trim();
  if (!trimmedUserId || !trimmedCampaignId) {
    return;
  }

  const memoryKey = `${trimmedUserId}:${trimmedCampaignId}`;
  memoryIntentSynced.add(memoryKey);

  try {
    sessionStorage.setItem(
      intentSyncedStorageKey(trimmedUserId, trimmedCampaignId),
      '1',
    );
  } catch {
    // memoryIntentSynced already updated
  }
}

export function wasCampaignIntentSynced(userId, campaignId) {
  const trimmedUserId = userId?.trim();
  const trimmedCampaignId = campaignId?.trim();
  if (!trimmedUserId || !trimmedCampaignId) {
    return false;
  }

  const memoryKey = `${trimmedUserId}:${trimmedCampaignId}`;
  if (memoryIntentSynced.has(memoryKey)) {
    return true;
  }

  try {
    return (
      sessionStorage.getItem(
        intentSyncedStorageKey(trimmedUserId, trimmedCampaignId),
      ) === '1'
    );
  } catch {
    return memoryIntentSynced.has(memoryKey);
  }
}

/** @internal Test helper */
export function __resetCampaignPromoStateForTests() {
  memoryPromoSeries = null;
  memoryIntentSynced.clear();
}

/** @internal Test helper — webpack bakes this at build; tests cannot change it */
export function __getBuildTimePromoSeriesForTests() {
  return BUILD_TIME_PROMO_SERIES;
}
